import './SatelliteSelectionModal.scss'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as Cesium from 'cesium'
import { Form } from 'react-bootstrap'
import {
    FooterAction,
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../../../CommonComponents/modals/Modal'
import { CloseSvg, InfoSvg } from '../../../../../CommonComponents/svgs/Svgs'
import useModal from '../../../hooks/useModal'
import useUserData from '../../../hooks/useUserData'
import useForm from '../../../hooks/useForm'
import useStaticData from '../../../hooks/useStaticData'
import useViewer from '../../../hooks/useViewer'
import { getSatelliteColor, preventEnterKey, showToastMessage } from '../../../../../../utils/commonFunctions'
import { FormGroupDetailsType, GroupOptionType } from '../../../utils/scenario-types'
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip'
import { getValidEpoch } from '../../../utils/helperFunction'
import {
    buildMultiGroupPreviewCzml,
    GroupPreviewParams,
    loadPreviewDataSource,
    removePreviewWithRef,
} from '../../../../../modelling/utils/orbitCalculations'

const PREVIEW_DS_NAME = '__scenario_orbit_preview__';

const SatelliteSelectionModal = () => {
    const { userData } = useUserData()
    const { allGroups } = useStaticData()
    const { modals, setModals } = useModal()
    const { formData, setFormData } = useForm()
    const { viewer } = useViewer()

    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime])
    const checkedGroups: FormGroupDetailsType[] = useMemo(() => modals?.configured?.data?.checkedGroups || [], [modals?.configured?.data?.checkedGroups])
    const checkedSat: GroupOptionType['satellites'][0] = useMemo(() => modals?.configured?.data?.checkedSat || null, [modals?.configured?.data?.checkedSat])

    const open = useMemo(() => modals?.configured?.name === 'satellite', [modals?.configured?.name])

    const maxLimit = useMemo(() => userData?.['feature-allowed']?.['noOfSimulationsMaxLimit'], [userData])
    const scenarioOperationType = useMemo(() => formData.details.scenarioOperation, [formData.details.scenarioOperation])
    const scenarioDeploymentType = useMemo(() => formData.details.scenarioDeploymentType, [formData.details.scenarioDeploymentType])

    // ─── Orbit preview logic ───
    const dsRef = useRef<Cesium.CzmlDataSource | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const groupTypeMap = useMemo(() => {
        const m: Record<string, GroupOptionType> = {};
        for (const g of allGroups) m[g.groupId] = g;
        return m;
    }, [allGroups]);

    const removePreview = useCallback(() => {
        removePreviewWithRef(viewer, dsRef, PREVIEW_DS_NAME);
    }, [viewer]);

    const loadPreview = useCallback((groups: FormGroupDetailsType[]) => {
        if (!viewer || groups.length === 0) { removePreview(); return; }

        const groupParams: GroupPreviewParams[] = groups
            .filter(gp => gp.referenceOrbit)
            .map(gp => {
                const orbit = gp.referenceOrbit;
                const fullGroup = groupTypeMap[gp.groupId];
                const isPlanePhasing = gp.phasingType === 'PlanePhasing';
                const isMulti = isPlanePhasing
                    ? gp.satCount > 1
                    : (fullGroup?.groupType === 'Multi Plane' || gp.noOfPlanes > 1);
                return {
                    groupId: gp.groupId,
                    groupName: gp.groupName,
                    altitude: Number(orbit.altitude),
                    eccentricity: Number(orbit.eccentricity),
                    inclination: Number(orbit.inclination),
                    RAAN: Number(orbit.RAAN),
                    AP: Number(orbit.AP),
                    TA: Number(orbit.TA),
                    epoch: Number(orbit.epoch),
                    startTime: Number(scenarioStartTime),
                    satCount: gp.satCount,
                    noOfPlanes: isPlanePhasing ? gp.satCount : gp.noOfPlanes,
                    noOfSatellitesPerPlane: isPlanePhasing ? 1 : gp.noOfSatellitesPerPlane,
                    phasingAngle: isPlanePhasing ? 0 : gp.phasingAngle,
                    planeSeparationAngle: isPlanePhasing ? gp.phasingAngle : gp.separationAngleBetweenPlane,
                    isMultiPlane: isMulti,
                    color: gp.color,
                };
            });

        const czml = buildMultiGroupPreviewCzml(groupParams, PREVIEW_DS_NAME);
        removePreview();
        dsRef.current = loadPreviewDataSource(viewer, czml, PREVIEW_DS_NAME);
    }, [viewer, removePreview, groupTypeMap, scenarioStartTime]);

    // Re-render orbits whenever checkedGroups changes
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!viewer || !open) { removePreview(); return; }
        if (checkedGroups.length === 0) { removePreview(); return; }
        debounceRef.current = setTimeout(() => loadPreview(checkedGroups), 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewer, open, checkedGroups, loadPreview, removePreview]);

    // Cleanup preview on unmount / modal close
    useEffect(() => {
        return () => { removePreview(); if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [removePreview]);

    const flagGroup = useMemo(() => userData?.['feature-allowed']?.['satelliteGroups'] === 'Yes' ? true : false, [userData]);
    const checkBoxType = useMemo(() => (flagGroup && scenarioOperationType !== 'LEOP' && scenarioDeploymentType !== 'HARDWARE_IN_LOOP') ? 'checkbox' : 'radio', [flagGroup, scenarioOperationType, scenarioDeploymentType])
    const SatelliteOptions = useMemo(() => scenarioOperationType === 'LEOP' ? allGroups.filter(sat => sat?.satCount === 1) : allGroups, [allGroups, scenarioOperationType])

    useEffect(() => {
        const allElements = document.querySelectorAll('.group__list__dropdown__container')
        const clickedOutside = (event: any) => {
            allElements.forEach(element => {
                if (!event.target.closest('.group__list__dropdown__container') && !event.target.closest('.satellite__selection__container')) {
                    element?.classList.remove('open')
                }
            })
        }
        document.addEventListener('mousedown', clickedOutside)
        return () => {
            document.removeEventListener('mousedown', clickedOutside)
        }
    }, [])

    const toggleDropdown = (elementId: string) => {
        const allElements = document.querySelectorAll('.group__list__dropdown__container')
        allElements.forEach(element => {
            if (element.id === elementId) {
                element?.classList.toggle('open')
            }
        })
    }

    const closeModal = () => {
        removePreview()
        setModals((prev) => {
            return {
                ...prev,
                configured: null
            }
        })
    }

    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            satellites: checkedGroups
        }))
        closeModal()
    }

    const getUpdatedGroupData = (group: GroupOptionType, satellite?: GroupOptionType['satellites'][0]) => {
        const satelliteOrbit = (satellite as GroupOptionType['satellites'][0] & {
            orbitParameters?: {
                altitude?: number;
                inclination?: number;
                eccentricity?: number;
                RAAN?: number;
                AP?: number;
                AOP?: number;
                TA?: number;
                epoch?: number;
            }
        })?.orbitParameters;
        const referenceOrbit = satelliteOrbit ? {
            altitude: satelliteOrbit.altitude ?? group.orbitData.altitude,
            inclination: satelliteOrbit.inclination ?? group.orbitData.inclination,
            eccentricity: satelliteOrbit.eccentricity ?? group.orbitData.eccentricity,
            RAAN: satelliteOrbit.RAAN ?? group.orbitData.RAAN,
            AP: satelliteOrbit.AP ?? satelliteOrbit.AOP ?? group.orbitData.AP,
            TA: satelliteOrbit.TA ?? group.orbitData.TA,
            epoch: scenarioStartTime,
        } : {
            ...group.orbitData,
            epoch: scenarioStartTime,
        };

        return {
            groupId: group.groupId,
            groupName: group.groupName,
            satelliteId: satellite?.uniqueId || '',
            satelliteName: satellite?.name || '',
            satCount: satellite ? 1 : group.satCount,
            referenceOrbit,
            phasingType: group.phasingType,
            phasingAngle: group.phasingAngle,
            separationAngleBetweenPlane: group.separationAngleBetweenPlane,
            noOfPlanes: group.noOfPlanes,
            noOfSatellitesPerPlane: group.noOfSatellitesPerPlane,
            color: group.color
        }
    }

    // function for validating total satellite count
    const getTotalSatelliteCount = (previous, current) => {
        let totalSatelliteCount: any = 0
        if (previous?.length > 0) {
            previous?.map((singleSat: GroupOptionType) => {
                totalSatelliteCount = totalSatelliteCount + singleSat?.satCount
            })
        }
        totalSatelliteCount = totalSatelliteCount + parseFloat(current?.satCount)
        return parseFloat(totalSatelliteCount)
    }

    const handleCheckBoxChange = (group: GroupOptionType) => {
        if (flagGroup && scenarioOperationType !== 'LEOP') {
            setModals((prev) => {
                const updatedGroup = getUpdatedGroupData(group)
                const alreadySelected = prev.configured?.data?.checkedGroups.some((g) => g?.groupId === group?.groupId);
                const reachedLimit = getTotalSatelliteCount(prev.configured?.data?.checkedGroups, updatedGroup) > maxLimit
                const groups = alreadySelected ? prev.configured?.data?.checkedGroups.filter((g) => g?.groupId !== group?.groupId) : !reachedLimit ? [...prev.configured?.data?.checkedGroups, updatedGroup] : [...prev.configured?.data?.checkedGroups]
                if (reachedLimit) {
                    showToastMessage('info', `Maximum satellite limit of ${maxLimit} reached. Contact your Administrator for support.`)
                }
                return {
                    ...prev!,
                    configured: {
                        ...prev.configured!,
                        data: {
                            ...prev.configured?.data!,
                            checkedGroups: groups
                        }
                    }
                }
            })
        } else {
            setModals((prev) => {
                const updatedGroup = getUpdatedGroupData(group)
                const alreadySelected = prev.configured?.data?.checkedGroups.some((g) => g?.groupId === group?.groupId);
                const groups = alreadySelected ? prev.configured?.data?.checkedGroups.filter((g) => g?.groupId !== group?.groupId) : [updatedGroup]
                return {
                    ...prev!,
                    configured: {
                        ...prev.configured!,
                        data: {
                            ...prev.configured?.data,
                            checkedGroups: groups
                        }
                    }
                }
            })
        }
    };

    const handleGroupClicked = (group: GroupOptionType,) => {
        if (scenarioDeploymentType === 'HARDWARE_IN_LOOP') {
            toggleDropdown(group?.groupId)
        } else {
            handleCheckBoxChange(group)
        }
    }

    const handleHybridGroupClicked = (group: GroupOptionType, satellite: GroupOptionType['satellites'][0]) => {
        const selected_satellite = checkedSat?.uniqueId === satellite?.uniqueId ? null : satellite
        const updatedGroup = getUpdatedGroupData(group, satellite)
        setModals((prev) => ({
            ...prev!,
            configured: {
                ...prev?.configured!,
                data: {
                    ...prev?.configured?.data,
                    checkedGroups: [updatedGroup],
                    checkedSat: selected_satellite
                }
            }
        }))
    }

    if (!open) return null
    return (
        <Modal open={open} pinned={true} disabled={true} className={`scenario__satellite__selection__modal`}>
            <ModalHeader className='scenario__satellite__selection__modal__header'>
                <HeaderTitleContainer>
                    <HeaderTitle>
                        SATELLITES
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    <HeaderAction title='Close' onClick={closeModal}>
                        <CloseSvg />
                    </HeaderAction>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody>
                {flagGroup &&
                    <div className='information__container'>
                        <InfoSvg />
                        {scenarioOperationType === 'CONOPS' && <span className='information__text'>{scenarioDeploymentType === 'HARDWARE_IN_LOOP' ? 'You can only select Satellite Group with single satellite.' : `You can select up to ${maxLimit} Satellites.`}</span>}
                        {scenarioOperationType === 'LEOP' && <span className='information__text'>You can only select Satellite Group with single satellite.</span>}
                    </div>
                }
                <div className='scenario__satellite__selection__modal__body'>
                    <div className='selection__list__container'>
                        {SatelliteOptions?.length > 0 && SatelliteOptions.map((option, index) => {
                            const sat_type = option.satCount > 1 ? 'Group' : 'Satellite';
                            const color = option.color
                            const checked = checkedGroups.some((group) => group?.groupId === option?.groupId);
                            if (sat_type === 'Group') {
                                return (
                                    <div id={option?.groupId} className='group__list__dropdown__container' key={option?.groupId}>
                                        <button className={`group__list__dropdown__container__handler ${scenarioDeploymentType === 'HARDWARE_IN_LOOP' ? 'show__drop__icon' : ''}`} onClick={() => {
                                            handleGroupClicked(option)
                                        }}>
                                            <div className={`dropdown__handler__icon__container`}>
                                                <Form.Check
                                                    type={checkBoxType}
                                                    label=""
                                                    className="custom__checkbox__selection"
                                                    checked={checked}
                                                    onKeyDown={preventEnterKey}
                                                />
                                            </div>
                                            <div className='dropdown__handler__details__container'>
                                                <div className='group__colour__container' style={{ background: `rgba(${JSON.parse(color).join(",")})` }} />
                                                <span className='dropdown__handler__text'>
                                                    <CustomToolTip title={option?.groupName} placement='top-start'>{option?.groupName}</CustomToolTip>
                                                </span>
                                            </div>
                                            <span className='dropdown__handler__count'>
                                                <CustomToolTip title='No. of Satellites' placement='top'>
                                                    {option.satCount}
                                                </CustomToolTip>
                                            </span>
                                        </button>
                                        <div className='group__dropdown__list__container'>
                                            {option?.satellites && option?.satellites.map(sat => {
                                                const checked = checkedSat && checkedSat?.uniqueId === sat?.uniqueId ? true : false;
                                                return (
                                                    <button className='dropdown__list__item__container' key={sat?.uniqueId}
                                                        onClick={() => {
                                                            handleHybridGroupClicked(option, sat)
                                                        }}
                                                    >
                                                        <div className='dropdown__list__item__selection__container'>
                                                            <Form.Check
                                                                type={checkBoxType}
                                                                label=""
                                                                className="custom__checkbox__selection"
                                                                checked={checked}
                                                                onKeyDown={preventEnterKey}
                                                            />
                                                        </div>
                                                        <div className='dropdown__list__item__details__container'>
                                                            <span className='dropdown__list__item__text__container'>
                                                                <CustomToolTip title={sat?.name} placement='top-start'>{sat?.name}</CustomToolTip>
                                                            </span>
                                                        </div>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )
                            } else {
                                return (
                                    <button className='dropdown__list__item__container' key={option?.groupId}
                                        onClick={() => {
                                            if (scenarioDeploymentType === 'HARDWARE_IN_LOOP') {
                                                handleHybridGroupClicked(option, option?.satellites?.[0])
                                            } else {
                                                handleCheckBoxChange(option)
                                            }
                                        }}
                                    >
                                        <div className='dropdown__list__item__selection__container'>
                                            <Form.Check
                                                type={checkBoxType}
                                                label=""
                                                className="custom__checkbox__selection"
                                                checked={checked}
                                                onKeyDown={preventEnterKey}
                                            />
                                        </div>
                                        <div className='dropdown__list__item__details__container'>
                                            <div className='satellite__colour__container' style={{ background: `rgba(${JSON.parse(getSatelliteColor(index)).join(",")})` }} />
                                            <span className='dropdown__list__item__text__container'>
                                                <CustomToolTip title={option?.groupName} placement='top-start'>
                                                    {option?.groupName}
                                                </CustomToolTip>
                                            </span>
                                        </div>
                                        <div className='dropdown__handler__count'>
                                            <CustomToolTip title='No. of Satellites' placement='top'>
                                                {option?.satCount}
                                            </CustomToolTip>
                                        </div>
                                    </button>
                                )
                            }
                        })}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <FooterAction variant='outline__button'
                    onClick={closeModal}
                >
                    Cancel
                </FooterAction>
                <FooterAction variant='primary__button'
                    onClick={handleSave}
                    disabled={checkedGroups?.length === 0}
                >
                    Save
                </FooterAction>
            </ModalFooter>
        </Modal>
    )
}

export default SatelliteSelectionModal
